import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useApolloClient, useQuery } from "@apollo/client";
import type { Row } from "@tanstack/react-table";
import DetailsCard from "@/components/DetailsCard";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import MetricGrid from "@/components/MetricGrid";
import SectionHeading from "@/components/SectionHeading";
import SideSheetModal from "@/components/SideSheetModal";
import Table from "@/components/Table";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import type { Path } from "@/router";
import { toFormat } from "@/lib/dateHelpers";
import { COMMISSION_CAMPAIGNS_QUERY } from "@/pages/billing/finance/commission/campaigns/queries";
import { COMMISSION_PROGRAM_CONSENTS_QUERY } from "@/pages/billing/finance/commission/consents/queries";
import type { CommissionProgramConsentNode } from "@/pages/billing/finance/commission/consents/types";
import { COMMISSION_ENROLLMENTS_QUERY } from "@/pages/billing/finance/commission/enrollments/queries";
import { formatEnumLabel } from "@/pages/billing/finance/commission/_utils";
import { isCommissionMocksEnabled } from "@/pages/billing/finance/commission/_mocks/commissionMockLink";
import { personGrpIaaSummaryByPgaId } from "@/pages/billing/finance/commission/_mocks/personGrpFixtures";
import { useShowPersonGrpBenefitsTab } from "../_utils";
import { PERSON_GRP_CONTEXT_QUERY } from "./queries";
import { getFaaColumns } from "./_components/FaaTableColumns";
import IaaSummaryCard from "./_components/IaaSummaryCard";
import type { PersonGrpFaaRow } from "./types";

const ENROLLMENT_PAGE_SIZE = 20;
const MAX_ENROLLMENT_PAGES = 5;

export default function PersonGrpBenefitsTab() {
  const { pgaId } = useParams();
  const showGrpTab = useShowPersonGrpBenefitsTab();
  const client = useApolloClient();
  const columns = useMemo(() => getFaaColumns(), []);
  const [faaRows, setFaaRows] = useState<PersonGrpFaaRow[]>([]);
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [consentsError, setConsentsError] = useState<Error | null>(null);
  const [enrollmentPagesFetched, setEnrollmentPagesFetched] = useState(0);
  const enrollmentFetchCursorsRef = useRef<Set<string>>(new Set());
  const [selectedFaaRow, setSelectedFaaRow] = useState<PersonGrpFaaRow | null>(null);

  const {
    data: personData,
    loading: personLoading,
    error: personError
  } = useQuery(PERSON_GRP_CONTEXT_QUERY, {
    variables: { pgaId: pgaId! },
    skip: !pgaId || !showGrpTab
  });

  const {
    data: enrollmentData,
    loading: enrollmentLoading,
    error: enrollmentError,
    fetchMore: fetchMoreEnrollments
  } = useQuery(COMMISSION_ENROLLMENTS_QUERY, {
    variables: { first: ENROLLMENT_PAGE_SIZE, search: pgaId },
    skip: !pgaId || !showGrpTab
  });

  const {
    data: campaignsData,
    loading: campaignsLoading,
    error: campaignsError
  } = useQuery(COMMISSION_CAMPAIGNS_QUERY, {
    variables: { first: 20 },
    skip: !showGrpTab
  });

  const crmPersonId = personData?.person.id;
  const campaignNodes = useMemo(() => campaignsData?.commissionCampaigns.nodes ?? [], [campaignsData]);
  const enrollmentNodes = useMemo(() => enrollmentData?.commissionEnrollments.nodes ?? [], [enrollmentData]);
  const enrollment = enrollmentNodes.find((node) => node.person?.pgaId === pgaId);
  const enrollmentPageInfo = enrollmentData?.commissionEnrollments.pageInfo;

  useEffect(() => {
    setEnrollmentPagesFetched(0);
    enrollmentFetchCursorsRef.current = new Set();
  }, [pgaId]);

  useEffect(() => {
    // The enrollments query has no person-scoped argument, so page until the
    // exact PGA ID match is found (bounded to avoid runaway fetching).
    if (enrollmentLoading || enrollmentError || enrollment) return;
    const endCursor = enrollmentPageInfo?.endCursor;
    if (!enrollmentPageInfo?.hasNextPage || !endCursor) return;
    if (enrollmentFetchCursorsRef.current.has(endCursor)) return;
    if (enrollmentPagesFetched >= MAX_ENROLLMENT_PAGES) return;
    enrollmentFetchCursorsRef.current.add(endCursor);
    setEnrollmentPagesFetched((count) => count + 1);
    fetchMoreEnrollments({
      variables: { first: ENROLLMENT_PAGE_SIZE, search: pgaId, after: endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;
        return {
          commissionEnrollments: {
            ...fetchMoreResult.commissionEnrollments,
            nodes: [...previous.commissionEnrollments.nodes, ...fetchMoreResult.commissionEnrollments.nodes]
          }
        };
      }
    });
  }, [
    enrollmentLoading,
    enrollmentError,
    enrollment,
    enrollmentPageInfo,
    enrollmentPagesFetched,
    fetchMoreEnrollments,
    pgaId
  ]);

  useEffect(() => {
    if (!crmPersonId || campaignNodes.length === 0) {
      setFaaRows([]);
      setConsentsError(null);
      return;
    }

    let cancelled = false;
    setConsentsLoading(true);
    setConsentsError(null);

    // The consents query requires a campaign ID, so one query per campaign is
    // unavoidable. Settle all of them: keep rows from campaigns that succeed
    // and surface failures instead of silently emptying the FAA table.
    Promise.allSettled(
      campaignNodes.map((campaign) =>
        client
          .query({
            query: COMMISSION_PROGRAM_CONSENTS_QUERY,
            variables: { campaignId: campaign.id, crmPersonId, first: 20 }
          })
          .then((result) => ({
            campaign,
            nodes: (result.data?.commissionProgramConsents.nodes ?? []) as CommissionProgramConsentNode[]
          }))
      )
    )
      .then((results) => {
        if (cancelled) return;

        const rows: PersonGrpFaaRow[] = [];
        let firstError: unknown = null;
        for (const result of results) {
          if (result.status === "fulfilled") {
            for (const consent of result.value.nodes) {
              rows.push({
                ...consent,
                id: `${result.value.campaign.id}-${consent.id}`,
                campaignLabel: `${result.value.campaign.editionYear} ${result.value.campaign.program.name}`,
                partnerName: result.value.campaign.partner.name
              });
            }
          } else if (firstError === null) {
            firstError = result.reason;
          }
        }

        setFaaRows(rows);
        setConsentsError(
          firstError === null
            ? null
            : firstError instanceof Error
              ? firstError
              : new Error("Failed to load FAA records.")
        );
      })
      .finally(() => {
        if (!cancelled) {
          setConsentsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [campaignNodes, client, crmPersonId]);

  if (!showGrpTab) {
    return (
      <EmptyState
        title="GRP unavailable"
        caption="Commission GRP benefits are not enabled for your account."
        iconName="lock"
        className="mt-12"
      />
    );
  }

  const queryError = personError ?? enrollmentError ?? campaignsError;
  if (queryError) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load GRP Benefits"
        message={queryError.message}
        className="mt-12"
      />
    );
  }

  const enrollmentMetrics: MetricCardType<Path>[] = [
    {
      label: "Program",
      value: enrollment?.program.name ?? "Not enrolled",
      loading: personLoading || enrollmentLoading
    },
    {
      label: "Account status",
      value: enrollment ? formatEnumLabel(enrollment.accountStatus) : "—",
      loading: personLoading || enrollmentLoading
    }
  ];

  // IAA fixtures are dev-mock only. Until a real IAA query exists, non-mock
  // environments render the empty IAA card.
  const iaaSummary = pgaId && isCommissionMocksEnabled() ? (personGrpIaaSummaryByPgaId[pgaId] ?? null) : null;

  return (
    <div className="mt-12 flex flex-col gap-6">
      <MetricGrid metrics={enrollmentMetrics} hasHeading />

      <SectionHeading heading="Facility Authorization Agreements (FAA)" />
      {consentsError && faaRows.length === 0 && !consentsLoading ? (
        <ErrorBoundary variant="inline" title="Failed to Load FAA Records" message={consentsError.message} />
      ) : (
        <>
          {consentsError ? (
            <ErrorBoundary variant="inline" title="Some FAA Records Failed to Load" message={consentsError.message} />
          ) : null}
          <Table
            data={faaRows}
            columns={columns}
            loading={campaignsLoading || consentsLoading}
            onClickRow={(row: Row<PersonGrpFaaRow>) => setSelectedFaaRow(row.original)}
            renderNoDataEmptyState={() => (
              <EmptyState
                title="No FAA on file"
                caption="Facility authorization agreements will appear here when recorded for this member."
                iconName="description"
              />
            )}
          />
        </>
      )}

      <SideSheetModal
        isSidebarOpen={selectedFaaRow != null}
        setSidebarOpen={(open) => {
          if (!open) setSelectedFaaRow(null);
        }}
        title="Facility Authorization Agreement"
      >
        {selectedFaaRow ? (
          <DetailsCard
            heading={selectedFaaRow.campaignLabel}
            testId="faa-detail-side-sheet"
            options={[
              { overline: "Partner", headline: selectedFaaRow.partnerName, iconName: "handshake" },
              { overline: "Status", headline: formatEnumLabel(selectedFaaRow.status), iconName: "info" },
              {
                overline: "Attribution",
                headline: formatEnumLabel(selectedFaaRow.attributionModel),
                iconName: "account_tree"
              },
              {
                overline: "Ownership",
                headline: selectedFaaRow.ownership ? formatEnumLabel(selectedFaaRow.ownership) : "—",
                iconName: "badge"
              },
              { overline: "Starts", headline: toFormat(selectedFaaRow.startsAt), iconName: "today" },
              {
                overline: "Ends",
                headline: selectedFaaRow.endsAt ? toFormat(selectedFaaRow.endsAt) : "—",
                iconName: "event"
              }
            ]}
          />
        ) : null}
      </SideSheetModal>

      <SectionHeading heading="Individual Allocation Agreement (IAA)" />
      <IaaSummaryCard summary={iaaSummary} />
    </div>
  );
}
